/**
 * The Results section bar (DESIGN §3b): a sticky row of pills that jumps to a section and shows the one in view. A section that is not on the page this round has no pill.
 * Sections are the `[data-section]` blocks of the page; each carries `scroll-margin-top`, so a jump lands below the stuck bar and never behind it.
 */
import { useEffect, useRef, useState } from "react";

export const SECTIONS: readonly { readonly id: string; readonly label: string }[] = [
  { id: "expected", label: "What happened" },
  { id: "moved", label: "What moved it" },
  { id: "year", label: "Your year" },
  { id: "prices", label: "Prices" },
  { id: "scoreboard", label: "Scoreboard" },
  { id: "forecasts", label: "Forecasts and events" },
  { id: "reflection", label: "Reflection" },
];

const find = (id: string): HTMLElement | null => document.querySelector<HTMLElement>(`[data-section="${id}"]:not(:empty)`);

/** Sent before a jump so a section that is folded by default opens first (its listener is `useOpenOnJump`). */
export const OPEN_SECTION_EVENT = "gx-open-section";

/** Scrolls to a section of the page (the hero's "See what moved it", a limit chip), opening it first when it is folded. */
export const goToSection = (id: string): void => {
  window.dispatchEvent(new CustomEvent(OPEN_SECTION_EVENT, { detail: id }));
  requestAnimationFrame(() => requestAnimationFrame(() => find(id)?.scrollIntoView({ behavior: "smooth", block: "start" })));
};

/** A folded section's hook: `open` starts as given and turns true when a jump names the section. */
export function useOpenOnJump(id: string, startOpen: boolean): readonly [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(startOpen);
  useEffect(() => {
    const on = (e: Event): void => { if ((e as CustomEvent<string>).detail === id) setOpen(true); };
    window.addEventListener(OPEN_SECTION_EVENT, on);
    return () => window.removeEventListener(OPEN_SECTION_EVENT, on);
  }, [id]);
  return [open, setOpen];
}

export function SectionBar({ watch }: { readonly watch: string }) {
  const [present, setPresent] = useState<readonly string[]>([]);
  const [current, setCurrent] = useState("expected");
  const bar = useRef<HTMLElement>(null);
  // Which sections are on the page: read again whenever the page's blocks change (a year still loading, a card that draws late).
  useEffect(() => {
    const read = (): void => setPresent((was) => { const now = SECTIONS.filter((s) => find(s.id) !== null).map((s) => s.id); return now.length === was.length && now.every((id, i) => id === was[i]) ? was : now; });
    read();
    const root = bar.current?.parentElement;
    if (root === null || root === undefined) return undefined;
    const watcher = new MutationObserver(read);
    watcher.observe(root, { childList: true, subtree: true });
    return () => watcher.disconnect();
  }, [watch]);
  // The current section is the last one whose top has passed the line just under the bar.
  useEffect(() => {
    const onScroll = (): void => {
      const line = (bar.current?.getBoundingClientRect().bottom ?? 0) + 24;
      const passed = present.filter((id) => (find(id)?.getBoundingClientRect().top ?? Infinity) <= line);
      setCurrent(passed.at(-1) ?? present[0] ?? "expected");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [present]);
  // The active chip stays in view in the scrolling row (a phone), and the row's right fade goes when its end is reached. Only the row scrolls, never the page.
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    const nav = bar.current;
    if (nav === null) return;
    const chip = nav.querySelector<HTMLElement>('[aria-current="true"]');
    if (chip !== null && nav.scrollWidth > nav.clientWidth) nav.scrollTo({ left: chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2, behavior: "smooth" });
  }, [current, present]);
  useEffect(() => {
    const nav = bar.current;
    if (nav === null) return undefined;
    const read = (): void => setAtEnd(nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 4);
    read();
    nav.addEventListener("scroll", read, { passive: true });
    return () => nav.removeEventListener("scroll", read);
  }, [present]);
  if (present.length < 3) return <nav ref={bar} hidden aria-label="Sections of this page" />;
  return (
    <nav ref={bar} className="gx-rbar" data-end={atEnd} aria-label="Sections of this page" data-testid="results.index">
      {SECTIONS.filter((s) => present.includes(s.id)).map((s) => (
        <button key={s.id} type="button" className="gx-rbar-pill" aria-current={s.id === current ? "true" : undefined} data-testid={`results.index.${s.id}`} onClick={() => goToSection(s.id)}>{s.label}</button>
      ))}
    </nav>
  );
}
